// built-from: /engagement-activity-execution v1 impl:engagement-activity-execution role:implementation
// built-from: /knowledge-files-view v1 impl:knowledge-files-view role:implementation
// built-from: /shared-process-spine v1 impl:shared-process-spine role:implementation
import { useCallback, useEffect, useRef, useState } from 'react';
import { useEngagement } from './useEngagement';

export type CharterState = 'unknown' | 'provisional' | 'agreed';
export type SetupLifecycle = 'draft' | 'active';

export interface CharterItem {
  value: string;
  state: CharterState;
  provenance?: Record<string, unknown>;
}

export interface EngagementRole {
  id: string;
  role: 'consultantLead' | 'clientSponsor' | 'clientCoordinator';
  name: string;
  title?: string;
  contact?: string;
  linkedUserId?: string | null;
}

export interface EngagementActivity {
  id: string;
  plan: 'engagement';
  name: string;
  purpose: string;
  owner: string;
  expectedOutput: string;
  participants: string[];
  dependencies: string[];
  specialistMethod?: string | null;
  scheduledStart?: string | null;
  state: string;
  order: number;
  /** 090: the page this activity's record is filed at, present once complete. */
  filedPath?: string;
}

export interface ProcessSpineStep {
  stepId: string;
  label: string;
  order: number;
  parentStepId: string | null;
  alternative?: { groupId: string; label: string };
  trigger?: { kind: string; text: string };
  latest?: boolean;
  needsLook?: boolean;
  provenance?: Record<string, unknown>;
}

export interface ProcessSpine {
  spineId: string;
  revision: number;
  phase: 'narration' | 'refinement';
  reviewState: 'provisional' | 'agreed' | 'needs-look';
  steps: ProcessSpineStep[];
  tombstones: Array<{ stepId: string; needsOverlayReview?: boolean }>;
}

export interface EngagementSetup {
  version: 1;
  lifecycle: SetupLifecycle;
  charter: {
    purpose: CharterItem;
    whyNow: CharterItem;
    success: CharterItem;
    scopeAndConstraints: CharterItem;
  };
  roles: EngagementRole[];
  activities: EngagementActivity[];
  agreement: {
    state: 'notReviewed' | 'agreedOutsideArcPilot' | 'revisionRequested';
    recordedBy?: string;
    recordedAt?: string;
    decidedAt?: string;
    note?: string;
  };
  changes: Array<Record<string, unknown>>;
  charterFacilitationSessionId: string | null;
  briefFacilitationSessionId?: string | null;
  processSpines: ProcessSpine[];
  activitySessions: Array<{
    plan: 'engagement';
    activityId: string;
    specialistId: string | null;
    sessionId: string;
    commandId: string;
    spineId?: string;
    annotations?: Array<Record<string, unknown>>;
  }>;
  activityOutputs: Array<{
    id: string;
    name: string;
    summary: string;
    notes?: string;
    sessionId: string;
    returnedAt: string;
    commandId: string;
    source: { plan: 'engagement'; activityId: string };
  }>;
  activation?: {
    commandId: string;
    activatedAt: string;
    publishedActivityIds: string[];
  };
}

interface SetupEnvelope {
  setup: EngagementSetup;
  revision: string | null;
  legacy?: boolean;
  activationState?: string;
}

// 090-engagement-knowledge-tree: completing an activity files its record in the
// engagement's knowledge base and reports where it landed, or that it could not
// be written. The workspace shows that outcome; nothing is swallowed.
export interface ActivityFilingOutcome {
  activityId: string;
  status: 'filed' | 'failed';
  path: string;
  message?: string;
}

function readFiling(body: unknown, activityId: string): ActivityFilingOutcome | null {
  const filing = (body as { filing?: unknown } | null)?.filing as {
    status?: unknown;
    path?: unknown;
    message?: unknown;
  } | undefined;
  if (!filing || (filing.status !== 'filed' && filing.status !== 'failed')) return null;
  return {
    activityId,
    status: filing.status,
    path: typeof filing.path === 'string' ? filing.path : '',
    message: typeof filing.message === 'string' ? filing.message : undefined,
  };
}

interface ApiFailure {
  code?: string;
  error?: string;
  missing?: string[];
  statusCode?: number;
}

export interface EngagementSetupError {
  code: string;
  message: string;
  missing?: string[];
}

function legacyActiveSetup(): EngagementSetup {
  return {
    version: 1,
    lifecycle: 'active',
    charter: {
      purpose: { value: '', state: 'unknown' },
      whyNow: { value: '', state: 'unknown' },
      success: { value: '', state: 'unknown' },
      scopeAndConstraints: { value: '', state: 'unknown' },
    },
    roles: [],
    activities: [],
    agreement: { state: 'notReviewed' },
    changes: [],
    charterFacilitationSessionId: null,
    processSpines: [],
    activitySessions: [],
    activityOutputs: [],
  };
}

function commandId(prefix: string): string {
  try {
    return `${prefix}-${crypto.randomUUID()}`;
  } catch {
    return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  }
}

async function api<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, init);
  const parsed = await response.json().catch(() => ({})) as (T & ApiFailure) | null;
  const body = (parsed && typeof parsed === 'object' ? parsed : {}) as T & ApiFailure;
  if (!response.ok) {
    const error = new Error(body.error || 'ArcPilot could not complete that action') as Error & ApiFailure;
    error.code = body.code || 'ENGAGEMENT_STORAGE_FAILURE';
    error.missing = body.missing;
    error.statusCode = response.status;
    throw error;
  }
  return body;
}

function failureMessage(failure: Error & ApiFailure): string {
  switch (failure.code) {
    case 'ENGAGEMENT_SETUP_CONFLICT':
      return 'This engagement changed since you opened it. Load the latest version and try again.';
    case 'ENGAGEMENT_SETUP_INCOMPLETE':
      return 'Complete the brief, people, activities and sponsor decision before starting.';
    case 'ENGAGEMENT_ALREADY_ACTIVE':
      return 'This engagement is already active.';
    case 'PROCESS_SPINE_CONFLICT':
      return 'The process changed while you were working. Load the latest sequence and try again.';
    case 'INVALID_PROCESS_SPINE':
    case 'INVALID_PROCESS_SPINE_COMMAND':
      return 'That process change needs a look before it can be saved.';
    case 'ENGAGEMENT_NOT_ACTIVE':
      return 'Start the engagement before opening this activity.';
    case 'INVALID_ENGAGEMENT_CHANGE':
    case 'INVALID_ENGAGEMENT_SETUP':
      return 'Check the information and try again.';
    case 'ENGAGEMENT_ACTIVATION_RECOVERY_REQUIRED':
      return 'ArcPilot could not finish setup. The engagement is active and your information is safe. Try again.';
    default:
      return "We couldn't save the engagement. Your entries have not been lost. Try again.";
  }
}

export function useEngagementSetup() {
  const { currentEngagementId, refreshEngagement } = useEngagement();
  const [setup, setSetup] = useState<EngagementSetup | null>(null);
  const [revision, setRevisionState] = useState<string | null>(null);
  // A ref mirror of `revision`, updated synchronously alongside the state
  // setter. A handler that awaits one hook call and then invokes another off
  // the SAME captured `model` reference (e.g. save() then activate() in one
  // click handler) would otherwise send the pre-await, now-stale `revision`
  // closure value as `expectedRevision` — the server always rotates the
  // revision on a successful write, so that stale value is deterministically
  // rejected as a conflict. Reading through the ref instead of the captured
  // state value keeps every revision-bearing call current regardless of which
  // render's closure invoked it.
  const revisionRef = useRef<string | null>(null);
  const setRevision = useCallback((next: string | null) => {
    revisionRef.current = next;
    setRevisionState(next);
  }, []);
  const [legacy, setLegacy] = useState(false);
  const [activationState, setActivationState] = useState<string | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'saving' | 'activating'>('loading');
  const [error, setError] = useState<EngagementSetupError | null>(null);
  const [activityFiling, setActivityFiling] = useState<ActivityFilingOutcome | null>(null);
  const activationCommandRef = useRef(commandId('activate'));
  const charterCommandRef = useRef(commandId('charter'));
  const briefCommandRef = useRef(commandId('brief'));
  const activityCommandsRef = useRef(new Map<string, string>());

  const load = useCallback(async () => {
    if (!currentEngagementId) return;
    setStatus('loading');
    setError(null);
    try {
      const envelope = await api<SetupEnvelope>(
        `/api/engagements/${encodeURIComponent(currentEngagementId)}/setup`,
      );
      if (!envelope.setup) {
        setSetup(legacyActiveSetup());
        setRevision(null);
        setLegacy(true);
        return;
      }
      setSetup(envelope.setup);
      setRevision(envelope.revision);
      setLegacy(Boolean(envelope.legacy));
      setActivationState(envelope.activationState || null);
      if (envelope.activationState === 'recovery-required') {
        setError({
          code: 'ENGAGEMENT_ACTIVATION_RECOVERY_REQUIRED',
          message: 'ArcPilot could not finish setup. The engagement is active and your information is safe. Try again.',
        });
      }
      activationCommandRef.current = envelope.setup.activation?.commandId || commandId('activate');
    } catch (cause) {
      const failure = cause as Error & ApiFailure;
      if (failure.statusCode === 404) {
        setSetup(legacyActiveSetup());
        setRevision(null);
        setLegacy(true);
        return;
      }
      setError({ code: failure.code || 'ENGAGEMENT_STORAGE_FAILURE', message: failureMessage(failure) });
    } finally {
      setStatus('ready');
    }
  }, [currentEngagementId, setRevision]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = useCallback(async (next: EngagementSetup): Promise<boolean> => {
    if (!currentEngagementId || setup?.lifecycle === 'active') return false;
    setStatus('saving');
    setError(null);
    setSetup(next);
    try {
      const envelope = await api<SetupEnvelope>(
        `/api/engagements/${encodeURIComponent(currentEngagementId)}/setup`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            charter: next.charter,
            roles: next.roles,
            activities: next.activities,
            agreement: next.agreement,
            charterFacilitationSessionId: next.charterFacilitationSessionId,
            expectedRevision: revisionRef.current,
          }),
        },
      );
      setSetup(envelope.setup);
      setRevision(envelope.revision);
      return true;
    } catch (cause) {
      const failure = cause as Error & ApiFailure;
      setError({
        code: failure.code || 'ENGAGEMENT_STORAGE_FAILURE',
        message: failureMessage(failure),
        missing: failure.missing,
      });
      return false;
    } finally {
      setStatus('ready');
    }
  }, [currentEngagementId, setRevision, setup?.lifecycle]);

  const activate = useCallback(async (): Promise<boolean> => {
    if (!currentEngagementId) return false;
    setStatus('activating');
    setError(null);
    const submit = () => api<SetupEnvelope & { activation: unknown }>(
        `/api/engagements/${encodeURIComponent(currentEngagementId)}/activate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            commandId: activationCommandRef.current,
            expectedRevision: revisionRef.current,
          }),
        },
      );
    try {
      const envelope = await submit();
      setSetup(envelope.setup);
      setRevision(envelope.revision);
      setActivationState('active');
      await refreshEngagement();
      return true;
    } catch (cause) {
      const failure = cause as Error & ApiFailure;
      const nextError = {
        code: failure.code || 'ENGAGEMENT_STORAGE_FAILURE',
        message: failureMessage(failure),
        missing: failure.missing,
      };
      if (nextError.code === 'ENGAGEMENT_ACTIVATION_RECOVERY_REQUIRED') {
        setActivationState('finishing');
        setError(null);
        await new Promise((resolve) => setTimeout(resolve, 0));
        try {
          const repaired = await submit();
          setSetup(repaired.setup);
          setRevision(repaired.revision);
          setActivationState('active');
          await refreshEngagement();
          return true;
        } catch {
          setActivationState('recovery-required');
          setError({
            code: 'ENGAGEMENT_ACTIVATION_RECOVERY_REQUIRED',
            message: 'ArcPilot could not finish setup. The engagement is active and your information is safe. Try again.',
          });
          return false;
        }
      }
      setError(nextError);
      return false;
    } finally {
      setStatus('ready');
    }
  }, [currentEngagementId, refreshEngagement, setRevision]);

  const change = useCallback(async (
    command: Record<string, unknown>,
  ): Promise<boolean> => {
    if (!currentEngagementId) return false;
    setStatus('saving');
    setError(null);
    try {
      const envelope = await api<SetupEnvelope>(
        `/api/engagements/${encodeURIComponent(currentEngagementId)}/setup/commands`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...command, expectedRevision: revisionRef.current }),
        },
      );
      setSetup(envelope.setup);
      setRevision(envelope.revision);
      return true;
    } catch (cause) {
      const failure = cause as Error & ApiFailure;
      setError({ code: failure.code || 'ENGAGEMENT_STORAGE_FAILURE', message: failureMessage(failure) });
      return false;
    } finally {
      setStatus('ready');
    }
  }, [currentEngagementId, setRevision]);

  const changeProcessSpine = useCallback(async (
    command: Record<string, unknown> & { expectedRevision: number },
  ): Promise<boolean> => {
    if (!currentEngagementId) return false;
    setStatus('saving');
    setError(null);
    try {
      const envelope = await api<SetupEnvelope>(
        `/api/engagements/${encodeURIComponent(currentEngagementId)}/setup/commands`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...command,
            commandId: commandId('process-spine'),
            spineExpectedRevision: command.expectedRevision,
            expectedRevision: revisionRef.current,
          }),
        },
      );
      // Only the server response acknowledges capture. A failed commit leaves
      // the last confirmed spine visible and the caller can mark it for review.
      setSetup(envelope.setup);
      setRevision(envelope.revision);
      return true;
    } catch (cause) {
      const failure = cause as Error & ApiFailure;
      setError({ code: failure.code || 'ENGAGEMENT_STORAGE_FAILURE', message: failureMessage(failure) });
      return false;
    } finally {
      setStatus('ready');
    }
  }, [currentEngagementId, setRevision]);

  const ensureCharterSession = useCallback(async (): Promise<string | null> => {
    if (!currentEngagementId || !setup) return null;
    if (setup.charterFacilitationSessionId) return setup.charterFacilitationSessionId;
    try {
      const created = await api<SetupEnvelope & { sessionId: string }>(
        `/api/engagements/${encodeURIComponent(currentEngagementId)}/charter/session/ensure`,
        {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          commandId: charterCommandRef.current,
          expectedRevision: revisionRef.current,
        }),
      },
      );
      setSetup(created.setup);
      setRevision(created.revision);
      return created.sessionId;
    } catch (cause) {
      const failure = cause as Error & ApiFailure;
      setError({ code: failure.code || 'ENGAGEMENT_STORAGE_FAILURE', message: failureMessage(failure) });
      return null;
    }
  }, [currentEngagementId, setRevision, setup]);

  // 075: quiet re-read after brief-facilitation hydration — refreshes the plan
  // and revision WITHOUT flipping status to 'loading' (which would unmount the
  // screen mid-conversation).
  const refreshQuietly = useCallback(async (): Promise<void> => {
    if (!currentEngagementId) return;
    try {
      const envelope = await api<SetupEnvelope>(
        `/api/engagements/${encodeURIComponent(currentEngagementId)}/setup`,
      );
      if (!envelope.setup) return;
      setSetup(envelope.setup);
      setRevision(envelope.revision);
    } catch {
      // A failed quiet refresh is silent: the next explicit action reloads.
    }
  }, [currentEngagementId, setRevision]);

  // 075: the Brief conversation on a brief-first engagement.
  const ensureBriefSession = useCallback(async (): Promise<string | null> => {
    if (!currentEngagementId || !setup) return null;
    if (setup.briefFacilitationSessionId) return setup.briefFacilitationSessionId;
    try {
      const created = await api<SetupEnvelope & { sessionId: string }>(
        `/api/engagements/${encodeURIComponent(currentEngagementId)}/brief/session/ensure`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            commandId: briefCommandRef.current,
            expectedRevision: revisionRef.current,
          }),
        },
      );
      setSetup(created.setup);
      setRevision(created.revision);
      return created.sessionId;
    } catch (cause) {
      const failure = cause as Error & ApiFailure;
      setError({ code: failure.code || 'ENGAGEMENT_STORAGE_FAILURE', message: failureMessage(failure) });
      return null;
    }
  }, [currentEngagementId, setRevision, setup]);

  const ensureActivitySession = useCallback(async (activityId: string, context?: { processId: string; specialistMethod: string; streamId?: string }): Promise<string | null> => {
    if (!currentEngagementId || !setup) return null;
    const specialistId = setup.activities.find((activity) => activity.id === activityId)
      ?.specialistMethod || null;
    const commandKey = `${activityId}|${specialistId || 'no-specialist'}|${context?.processId || ''}|${context?.streamId || ''}`;
    const currentCommand = activityCommandsRef.current.get(commandKey) || commandId('activity');
    activityCommandsRef.current.set(commandKey, currentCommand);
    try {
      const created = await api<SetupEnvelope & { sessionId: string }>(
        `/api/engagements/${encodeURIComponent(currentEngagementId)}/activities/${encodeURIComponent(activityId)}/session/ensure`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ commandId: currentCommand, expectedRevision: revisionRef.current, ...context }),
        },
      );
      setSetup(created.setup);
      setRevision(created.revision);
      return created.sessionId;
    } catch (cause) {
      const failure = cause as Error & ApiFailure;
      setError({ code: failure.code || 'ENGAGEMENT_STORAGE_FAILURE', message: failureMessage(failure) });
      return null;
    }
  }, [currentEngagementId, setRevision, setup]);

  const returnActivityOutput = useCallback(async (
    activityId: string,
    sessionId: string,
    output: { id: string; name: string; summary: string; sharedSource?: { streamId: string; streamRevision?: string; processRevisions: Record<string, string> } },
  ): Promise<boolean> => {
    if (!currentEngagementId) return false;
    try {
      const returned = await api<SetupEnvelope>(
        `/api/engagements/${encodeURIComponent(currentEngagementId)}/activities/${encodeURIComponent(activityId)}/outputs/return`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            commandId: commandId('output'),
            expectedRevision: revisionRef.current,
            sessionId,
            output,
          }),
        },
      );
      setSetup(returned.setup);
      setRevision(returned.revision);
      return true;
    } catch (cause) {
      const failure = cause as Error & ApiFailure;
      setError({ code: failure.code || 'ENGAGEMENT_STORAGE_FAILURE', message: failureMessage(failure) });
      return false;
    }
  }, [currentEngagementId, setRevision]);

  const completeActivity = useCallback(async (
    activityId: string,
    input: { result: string; notes?: string },
  ): Promise<boolean> => {
    if (!currentEngagementId) return false;
    try {
      const completed = await api<SetupEnvelope>(
        `/api/engagements/${encodeURIComponent(currentEngagementId)}/activities/${encodeURIComponent(activityId)}/complete`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            commandId: commandId('complete'),
            expectedRevision: revisionRef.current,
            result: input.result,
            notes: input.notes,
          }),
        },
      );
      setSetup(completed.setup);
      setRevision(completed.revision);
      setActivityFiling(readFiling(completed, activityId));
      return true;
    } catch (cause) {
      const failure = cause as Error & ApiFailure;
      setError({ code: failure.code || 'ENGAGEMENT_STORAGE_FAILURE', message: failureMessage(failure) });
      return false;
    }
  }, [currentEngagementId, setRevision]);

  return {
    setup,
    setSetup,
    revision,
    legacy,
    activationState,
    status,
    error,
    loadLatest: load,
    save,
    activate,
    change,
    changeProcessSpine,
    ensureCharterSession,
    ensureBriefSession,
    ensureActivitySession,
    returnActivityOutput,
    completeActivity,
    activityFiling,
    setActivityFiling,
    refreshQuietly,
  };
}
